import { PaginationTable, TableWrapper } from 'components/Global';
import EmptyState from 'components/Global/EmptyState';
import LoadingSpinner from 'components/Global/LoadingSpinner';
import { deleteReview, getReviewById, getReviews, updateReviewVisibility } from 'features/reviews/actions';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as BannerIcon } from 'icons/banner-filled-icon.svg';
import { useSearchParams } from 'react-router-dom';
import { useAppContext } from 'components/Context/AppContext';
import FilterBar from './filter-bar';
import clsx from 'clsx';
import { ReactComponent as Star } from 'icons/star.svg';
import ActionButtons from 'components/Global/Table/ActionButtons';

const COLUMN = [
  { header: 'No.', value: 'no' },
  { header: 'Nomor Order', value: 'order_number' },
  { header: 'Review', value: 'comment' },
  { header: 'Jenis Layanan', value: 'order_type' },
  { header: 'Rating', value: 'rating' },
];

const ReviewPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page')) || 1;
  const order_number = searchParams.get('order_number') || '';
  const service = searchParams.get('service') || '';
  const rating = searchParams.get('rating') || '';

  const { setShowConfirmation, showToast } = useAppContext();

  const { list, isLoading } = useSelector((state) => state.reviews);
  const pageNumber = (page, idx) => (page > 1 ? (page - 1) * 10 + idx : idx);

  useEffect(() => {
    dispatch(getReviews({ page, order_number, service, rating }));
  }, [page, order_number, service, rating]);

  const handleDetail = (e) => {
    const id = e.target.parentElement.parentElement.dataset['key'];
    navigate(`/review/${id}`);
  };

  const handleDelete = (e) => {
    const id = e.target.parentElement.parentElement.dataset['key'];

    setShowConfirmation({
      message: 'Apakah anda ingin menghapus review ini?',
      show: true,
      onClick: async () => {
        try {
          await dispatch(deleteReview(id)).unwrap();
          showToast({ type: 'success', message: 'Berhasil menghapus review!' });
          dispatch(getReviews({ page, order_number, service, rating }));
        } catch (err) {
          showToast({ type: 'error', message: 'Gagal menghapus review!' });
        }
      },
    });
  };

  const handleReviewVisibility = async (e) => {
    const id = e.target.parentElement.parentElement.dataset['key'];

    try {
      const res = await dispatch(getReviewById(id)).unwrap();

      const MESSAGE = res.shown
        ? 'Anda yakin akan menyembunyikan review ini?'
        : 'Anda yakin akan menampilkan review ini?';

      setShowConfirmation({
        message: MESSAGE,
        show: true,
        onClick: async () => {
          try {
            await dispatch(updateReviewVisibility({ id, data: { is_shown: !res.shown } })).unwrap();
            dispatch(getReviews({ page, order_number, service, rating }));
          } catch (error) {
            showToast({ type: 'error', message: 'Gagal Mengubah visibilitas review' });
          }
        },
      });
    } catch (error) {
      showToast({ type: 'error', message: 'Gagal mengambil data review' });
    }
  };

  const DATA = list?.data?.map((item) => ({
    ...item,
    order_type: getOrderType({ orderType: item.order_type_id, supportDriver: item.support_driver }),
  }));

  const handlePageChange = (newPage) => {
    const updatedParams = Object.fromEntries(searchParams.entries());
    updatedParams.page = newPage;
    setSearchParams(updatedParams);
  };

  const rows = 10 - DATA?.length;

  return (
    <>
      <FilterBar />
      <div className="review">
        <TableWrapper title="Review" icon={<BannerIcon fill="#009EF7" width="25px" height="25px" />}>
          {isLoading ? (
            <LoadingSpinner />
          ) : list?.data?.length ? (
            <>
              <div className={clsx('table')}>
                <table className="table-wrapper">
                  <thead className="table-head">
                    <tr>
                      {COLUMN.map((item, i) => (
                        <th key={i}>{item.header}</th>
                      ))}
                      <th className="action-col">Action</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {DATA.length > 0 &&
                      DATA.map((item, no) => (
                        <tr key={no}>
                          {COLUMN.map((col, idx) => {
                            if (col.value === 'no') {
                              return (
                                <td
                                  key={idx}
                                  data-type="no"
                                  className={col?.highlight_color ? `col-highlight-${col?.highlight_color}` : ''}
                                >
                                  {pageNumber(page, no + 1)}
                                </td>
                              );
                            } else if (col.value === 'rating') {
                              return (
                                <td key={idx}>
                                  <div className="rating-wrapper">
                                    {Array(5)
                                      .fill(0)
                                      .map((_, index) => {
                                        return (
                                          <Star key={index} color={item[col.value] > index ? '#f1a33a' : '#edebeb'} />
                                        );
                                      })}
                                  </div>
                                </td>
                              );
                            } else if (col.value === 'order_number') {
                              return (
                                <td
                                  key={idx}
                                  data-type={col.value}
                                  className={col?.highlight_color ? `col-highlight-${col?.highlight_color}` : ''}
                                  style={{ fontWeight: '700' }}
                                >
                                  {item[col.value]}
                                </td>
                              );
                            } else if (col.value === 'comment') {
                              return (
                                <td
                                  key={idx}
                                  data-type={col.value}
                                  className={col?.highlight_color ? `col-highlight-${col?.highlight_color}` : ''}
                                  style={{ width: '300px', textAlign: 'left', padding: '0 18px' }}
                                >
                                  {item[col.value]}
                                </td>
                              );
                            } else {
                              return (
                                <td
                                  key={idx}
                                  data-type={col.value}
                                  className={col?.highlight_color ? `col-highlight-${col?.highlight_color}` : ''}
                                >
                                  {item[col.value]}
                                </td>
                              );
                            }
                          })}
                          <td
                            className="btn-action"
                            data-type="actionButtons"
                            data-key={!item.id ? item.code : item.id}
                          >
                            {showVisibilityButton(item.order_type_id) ? (
                              <ActionButtons show={{ state: item.shown, setState: handleReviewVisibility }} />
                            ) : null}

                            <ActionButtons detail={handleDetail} />

                            <ActionButtons del={handleDelete} />
                          </td>
                        </tr>
                      ))}
                    {rows > 0 &&
                      [...Array(rows)].map((_, idx) => (
                        <tr key={idx}>
                          {COLUMN.map((_, idx) => (
                            <td key={idx}></td>
                          ))}
                          <td className="btn-action"></td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <PaginationTable
                currentPage={page}
                onPageChange={handlePageChange}
                totalCount={list?.pagination?.total}
              />
            </>
          ) : (
            <EmptyState />
          )}
        </TableWrapper>
      </div>
    </>
  );
};

export default ReviewPage;

export const getOrderType = ({ orderType, supportDriver }) => {
  if (orderType === 1) {
    return supportDriver ? 'Dengan Supir' : 'Tanpa Supir';
  }

  if (orderType === 2) return 'Airport Transfer Bali';

  if (orderType === 7) return 'Airport Transfer Singapore';

  if (orderType === 8) return 'Car Charter';

  return '-';
};

const showVisibilityButton = (orderType) => {
  return orderType === 1;
};
